import { Icon } from '../../components/icons.js';
import { Component } from 'preact';
import { apply, formatPrice, maybe, parseURL } from '../../../common/util.js';
import { authenticateStoreCollection, getDesignsByCategory, getDesignsByCollection } from '../../lib/api.js';
import { Popup } from '../../components/popup.js';
import { classNames, getStoreUrl } from '../../lib/util.js';
import { Picture } from '../../components/image.js';
import { useState } from 'preact/hooks';
import { Input } from '../../components/input.js';
import { makeDesignPreviewUrl } from '../../lib/images.js';
import { PublicStore, PublicStoreCollection, StoreCollection, StoreProduct } from '../../../common/data/stores.js';
import { ProductCategory } from '../../../common/data/products.js';
import { socialWebsiteByUrl } from '../../lib/socials.js';
import { Id } from '../../../common/data.js';
import { currencyConfig } from '../../../common/data/countries.js';

export interface StorePageProps {
	store: PublicStore;
	cid?: string;
	categories: ProductCategory[];
	designs: StoreProduct[];
	collections: PublicStoreCollection[];
	colId?: string;
	freeUSShippingFrom: number;
	failedPassphrase?: boolean;
}

interface StorePageState {
	store: PublicStore;
	designs: StoreProduct[];
	categories?: ProductCategory[];
	cid?: string;
	colId?: string;
	popup?: 'passphrase' | undefined;
	colForPassphrase?: PublicStoreCollection | undefined;
	productSide: Record<Id<StoreProduct>, string>;
}

export class StorePage extends Component<StorePageProps, StorePageState> {

	constructor(props: StorePageProps) {
		super(props);
		this.state = {
			cid: props.cid,
			store: props.store,
			designs: props.designs,
			colId: props.colId,
			productSide: {},
		};

		if (this.props.failedPassphrase) {
			const collection = this.props.collections.find(c => c.id === this.props.colId);
			this.state = { ...this.state,
				popup: 'passphrase',
				colForPassphrase: collection };
		}
	}

	async changeCategory(e: Event, cid?: string) {
		const link = cid ? getStoreUrl(this.state.store, `/category/${cid}`) : getStoreUrl(this.state.store);
		e.preventDefault();
		history.replaceState({}, '', link);
		const { designs } = await getDesignsByCategory({ store: this.state.store.id, cid });
		this.setState({ cid, designs, colId: undefined });
	}

	async changeCollection(e: Event, collection?: string) {
		const link = collection ? getStoreUrl(this.state.store, `/collection/${collection}`) : getStoreUrl(this.state.store);
		e.preventDefault();
		history.replaceState({}, '', link);
		const { designs } = await getDesignsByCollection({ store: this.state.store.id, collection });
		this.setState({ colId: collection, designs, cid: undefined });
	}

	async changeToProtectedCollection() {
		const colId = this.state.colForPassphrase?.id;
		const link = getStoreUrl(this.state.store, `/collection/${colId}`);
		history.replaceState({}, '', link);
		const { designs } = await getDesignsByCollection({ store: this.state.store.id, collection: colId });
		this.setState({ colId, designs, cid: undefined, colForPassphrase: undefined, popup: undefined });
	}

	async showCollection(e: Event, collection: PublicStoreCollection) {
		if (collection.haspassphrase) {
			e.preventDefault();
			await getDesignsByCollection({ store: this.state.store.id, collection: collection.id })
				.then(({ designs }) => {
					const link = getStoreUrl(this.state.store, `/collection/${collection.id}`);
					history.replaceState({}, '', link);
					this.setState({ colId: collection.id, designs, cid: undefined });
				})
				.catch(e => {
					// FIXME
					this.setState({ popup: 'passphrase', colForPassphrase: collection });
				});
		}
		else await this.changeCollection(e, collection.id);
	}

	toggleSide = (sp: StoreProduct) => (e: MouseEvent) => {
		const cur = this.state.productSide[sp.id] ?? 'front';
		this.setState({
			productSide: {
				...this.state.productSide,
				[sp.id]: cur === 'front' ? 'back' : 'front',
			},
		});
	};

	render(props: StorePageProps, state: StorePageState) {
		const freeShipping = props.freeUSShippingFrom;
		const curConfig = maybe(props.store.display_currency, dcid => dcid != 'USD' ? currencyConfig[dcid] : null);
		return <main class="store" style={`--main-color: ${props.store.highlight_color || '#a4a4a4'}`}>
			{maybe(props.store.banners?.hero, banner => <div class="store-banner">
				<Picture
					src={`/data/stores/${banner.image}`}
					class="store-banner__img"
					style={{ height: `${(banner.height / banner.width * 100).toFixed(2)}vw` }}
					aria-hidden={true}
				/>
			</div>)}
			<p class="store-shipping-info">{freeShipping > 0 ? `Free US shipping on orders ${formatPrice(freeShipping)} or more`: 'Free US shipping on all orders'}</p>
			<div class="store-content">
				<div class="store-content__about">
					<div class="store-column store-column--detail">
						<div class="store-media">
							<h2 class="store-detail__heading">{props.store.name}</h2>
							<ul class="store-social-list">
								{props.store.links?.map(link => {
									const ws = socialWebsiteByUrl(link);
									return <li class="store-social-list__item">
										<a href={link} class="store-social-list__link" target="_blank" title={ws.name} rel="noreferrer">
											<Icon name={ws.icon} />
										</a>
									</li>;
								})}
							</ul>
						</div>
						<div class="il">
							<p class="store-detail__paragraph">
								{props.store.description}
							</p>
						</div>
					</div>
				</div>
				<div class="store-content__product">
					<div class="store-column store-column--left">
						<div class="store-group">
							<div class="store-group__heading">categories</div>
							<ul class="store-cat-list">
								<li class="store-cat-list__item">
									<a href={getStoreUrl(this.state.store)}
										onClick={e => this.changeCategory(e)}
										class={`store-cat-list__link ${!state.cid ? 'store-cat-list__link--active' : ''}`}>All categories</a>
								</li>
								{props.categories && props.categories.map(cat => <li class="store-cat-list__item"><a
									href={getStoreUrl(this.state.store, `/category/${cat.id}`)}
									onClick={e => this.changeCategory(e, cat.id)}
									class={`store-cat-list__link ${state.cid === cat.id ? 'store-cat-list__link--active' : ''}`}>{cat.name}</a></li>)}
							</ul>
						</div>
						{!!props.collections.length && <div class="store-group">
							<div class="store-group__heading">Collections</div>
							<ul class="store-cat-list">
								<li class="store-cat-list__item">
									<a href={getStoreUrl(this.state.store)}
										onClick={e => this.changeCollection(e)}
										class={`store-cat-list__link ${!state.colId ? 'store-cat-list__link--active' : ''}`}>All collections</a>
								</li>
								{props.collections.map(col => <li class="store-cat-list__item"><a
									href={getStoreUrl(this.state.store, `/collection/${col.id}`)}
									onClick={async e => await this.showCollection(e, col)}
									class={`store-cat-list__link ${state.colId === col.id ? 'store-cat-list__link--active' : ''}`}>{col.name}</a>{col.haspassphrase && <Icon name="lock" />}</li>)}
							</ul>
						</div>}
						{!!props.store.links?.length && <div class="store-group store-group--desktop">
							<div class="store-group__heading">Links</div>
							{props.store.links.map(link => {
								const ws = socialWebsiteByUrl(link);
								return <li class="store-social-list__item store-social-list__item-community">
									<a href={link} class="store-social-list__link store-social-list__link-community" target="_blank" rel="noreferrer">
										<Icon name={ws.icon} />
										<span class="store-social-list__hostname">{ws.name ?? parseURL(link).hostname}</span>
									</a>
								</li>;
							})}
						</div>}
					</div>
					<div class="store-column store-column--product">
						{!!state.designs.length && <div class="store-product-group">
							{state.designs.map(des => <div class="store-card" key={des.id}>
								<div class="store-card__badges">
									{maybe(des.product_sides, s => s.length > 1) && <button
										title="Toggle shown side"
										class={classNames('store-card__badge', '--side')}
										onClick={this.toggleSide(des)}
									><Icon name="reuse" /></button>}
								</div>
								<a class="store-card__image" href={getStoreUrl(this.state.store, `/${des.id}`)}>
									<Picture
										class="store-card__img store-card__img--wear"
										src={makeDesignPreviewUrl(
											des.design,
											{ side: state.productSide[des.id] ?? 'front' },
										)}
										alt={`${des.name} preview, ${state.productSide[des.id] ?? 'front'}`}
									/>
									{des.mask_image && <Picture
										class="product-shadow"
										src={`/data/product-masks/${des.mask_image}.front.shadow.png`}
										aria-hidden={true}
									/>}
								</a><p class="store-card__name" title={des.name}>{des.name}</p>
								<div class="store-card__action">
									<button class="store-card__product-price" onClick={() => location.href = getStoreUrl(this.state.store, `/${des.id}`)}>
										{(curConfig && maybe(des.display_price, p => curConfig.format.replace('@', p)))
									?? formatPrice(des.price)}
									</button>
								</div>
							</div>)}
						</div>}

					</div>
					<div class="store-column store-column--mobile">
						{!!props.store.links?.length && <div class="store-group store-group--mobile">
							<div class="store-group__heading">community</div>
							{props.store.links.map(link => {
								const ws = socialWebsiteByUrl(link);
								return <li class="store-social-list__item store-social-list__item--mobile">
									<a href={link} class="store-social-list__link store-social-list__link-community">
										<Icon name={ws.icon} />
									</a>
									<span class="store-social-list__hostname">{ws.name ?? parseURL(link).hostname}</span>
								</li>;
							})}
						</div>}
					</div>
				</div>
			</div>
			{state.popup == 'passphrase' && state.colForPassphrase && <Popup close={() => this.setState({ popup: undefined })}>
				<PassphrasePopup collection={state.colForPassphrase} onSuccess={() => this.changeToProtectedCollection() } />
			</Popup>}
		</main>;
	}
}

function PassphrasePopup({ collection, onSuccess }: {
	collection: StoreCollection;
	onSuccess: () => void;
}) {
	const [passphrase, setPassphrase] = useState('');
	const [error, setError] = useState('');

	const checkPassphrase = async (e: Event) => {
		e.preventDefault();
		// FIXME
		await authenticateStoreCollection({
			collection: collection.id,
			passphrase,
		}).then(onSuccess).catch(() => setError('Wrong passphrase. Try again!'));
	};

	return <div>
		<span class="store__collection-form-title">Enter Passphrase for {collection.name}</span>
		<form class="store__collection-form" onSubmit={checkPassphrase}>
			<div class="store__collection-form-password">
				<label>Password</label>
				<Input value={passphrase} handler={v => apply(v.trim().length > 0 ? v.trim() : '', x => setPassphrase(x))}/>
			</div>
			{error.length > 0 && <p>
				{error}
			</p>}
			<button type="submit">Unlock</button>
		</form>
	</div>;
}
